import React, { useState } from 'react';
import { Button } from '../ui/button';
import { X, ChevronLeft, ChevronRight, ChevronDown, CheckCircle2, Download, Infinity, Palette, Shield, ArrowRight, Eye } from 'lucide-react';
import { demoPages } from '../../data/demoPages';
import { getProductHeroImage } from '../../data/productHeroImages';
import FavoriteButton from '../FavoriteButton';
import RecommendationSection from '../RecommendationSection';

export const ProductPageModal = ({ product, onClose, onCreateProduct, onNextProduct }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [openFaq, setOpenFaq] = useState(null);
  const [selectedLicense, setSelectedLicense] = useState('personal');
  const demo = demoPages[product.title];

  // Escape key to close
  React.useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  if (!demo) return null;
  const pages = demo.pages;
  const accent = product.colors.accent;
  const heroImg = getProductHeroImage(product.slug || product.title.toLowerCase().replace(/\s+/g, '-'), 'hero');
  const hasDualPricing = product.personalPriceUsd != null && product.commercialPriceUsd != null;

  return (
    <div data-testid="preview-modal" className="fixed inset-0 z-50 overflow-y-auto">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative min-h-screen flex items-start justify-center py-4 md:py-8 px-3 md:px-0">
        <div className="relative w-full max-w-3xl bg-[#B49A88] rounded-2xl shadow-2xl overflow-hidden">
          <button data-testid="preview-modal-close" onClick={onClose} className="absolute top-5 right-5 z-20 w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center text-stone-400 hover:text-stone-800 transition-colors shadow-md hover:shadow-lg">
            <X className="w-5 h-5" />
          </button>

          {/* Hero lifestyle banner */}
          {heroImg && (
            <div className="relative h-[200px] md:h-[280px] overflow-hidden">
              <img src={heroImg.url} alt={heroImg.alt} className="w-full h-full object-cover" style={{ objectPosition: heroImg.focal || 'center' }} />
              <div className="absolute inset-0 bg-gradient-to-t from-[#B49A88] via-[#B49A88]/30 to-transparent" />
            </div>
          )}

          <div className="p-6 md:p-10 pb-0">
            <div className="grid md:grid-cols-2 gap-8 md:gap-12">
              <div>
                <div className="relative bg-white rounded-2xl shadow-lg overflow-hidden border border-stone-100">
                  <div className="aspect-[3/4]">{pages[currentPage]({ accent })}</div>
                </div>
                <div className="flex gap-2 mt-4 justify-center">
                  {pages.map((_, i) => (
                    <button key={i} onClick={() => setCurrentPage(i)} className={`w-14 h-14 rounded-xl overflow-hidden border-2 transition-all duration-200 ${i === currentPage ? 'border-primary shadow-md scale-105' : 'border-stone-200 opacity-60 hover:opacity-100'}`}>
                      <div className="w-full h-full scale-[0.25] origin-top-left" style={{ width: '400%', height: '400%' }}>{pages[i]({ accent })}</div>
                    </button>
                  ))}
                </div>
                <div className="flex items-center justify-center gap-3 mt-3">
                  <button data-testid="preview-prev-page" onClick={() => setCurrentPage(p => Math.max(0, p-1))} disabled={currentPage === 0} className="w-9 h-9 rounded-full bg-white shadow-sm border border-stone-100 flex items-center justify-center disabled:opacity-30 hover:shadow-md transition-all">
                    <ChevronLeft className="w-4 h-4 text-stone-600" />
                  </button>
                  <span className="text-xs font-sans text-stone-400">{currentPage + 1} / {pages.length}</span>
                  <button data-testid="preview-next-page" onClick={() => setCurrentPage(p => Math.min(pages.length-1, p+1))} disabled={currentPage === pages.length-1} className="w-9 h-9 rounded-full bg-white shadow-sm border border-stone-100 flex items-center justify-center disabled:opacity-30 hover:shadow-md transition-all">
                    <ChevronRight className="w-4 h-4 text-stone-600" />
                  </button>
                </div>
              </div>

              <div className="flex flex-col">
                {product.label && <span className={`self-start text-[10px] font-sans font-bold tracking-wider uppercase px-3 py-1 rounded-full mb-4 ${product.labelColor}`}>{product.label}</span>}
                <h2 className="text-2xl md:text-3xl font-serif font-bold text-stone-800 mb-2 tracking-tight">{product.title}</h2>
                <div className="flex items-center gap-3 mb-1">
                  <p className="text-sm font-sans text-stone-500">{product.sub}</p>
                  <FavoriteButton productTitle={product.title} size="lg" />
                </div>
                <p className="text-xs font-sans text-stone-400 mb-5">{demo.includes}</p>
                <div className="mb-6" />

                {/* License Tier Selector */}
                {hasDualPricing && (
                  <div data-testid="license-selector" className="space-y-2 mb-5">
                    <button data-testid="select-personal" onClick={() => {
                      setSelectedLicense('personal');
                      sessionStorage.setItem('lumera_license_recommendation', 'personal');
                    }}
                      className={`w-full text-left p-4 rounded-xl border-2 transition-all ${selectedLicense === 'personal' ? 'border-[#C49A6C] bg-[#F5F0EB]/60 shadow-sm' : 'border-stone-200 hover:border-stone-300'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          {selectedLicense === 'personal' && <CheckCircle2 className="w-4 h-4 text-[#C49A6C]" />}
                          <span className="font-sans font-semibold text-stone-800 text-sm">Personal Use</span>
                        </div>
                        <span className="font-serif font-bold text-stone-900">${product.personalPriceUsd?.toFixed(2)}</span>
                      </div>
                      <p className="text-xs font-sans text-stone-500">For your own personal use. No resale or commercial use.</p>
                    </button>
                    <button data-testid="select-commercial" onClick={() => {
                      setSelectedLicense('commercial');
                      sessionStorage.setItem('lumera_license_recommendation', 'commercial');
                    }}
                      className={`w-full text-left p-4 rounded-xl border-2 transition-all ${selectedLicense === 'commercial' ? 'border-[#C49A6C] bg-[#F5F0EB]/60 shadow-sm' : 'border-stone-200 hover:border-stone-300'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-sans font-semibold text-stone-800 text-sm">Commercial License</span>
                        <span className="font-serif font-bold text-stone-900">${product.commercialPriceUsd?.toFixed(2)}</span>
                      </div>
                      <p className="text-xs font-sans text-stone-500">For creators and businesses. Commercial License included.</p>
                      <div className="flex items-center gap-1 mt-1.5">
                        <Shield className="w-3 h-3 text-[#8B7B6B]" />
                        <span className="text-[10px] font-sans font-medium text-[#8B7B6B]">Commercial License included</span>
                      </div>
                    </button>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-2 mb-6">
                  {[
                    { icon: Download, text: 'Instant download' },
                    { icon: Infinity, text: 'Lifetime access' },
                    { icon: Palette, text: 'Canva editable' },
                    { icon: Shield, text: selectedLicense === 'commercial' ? 'Commercial license' : 'Personal use license' },
                  ].map(b => (
                    <div key={b.text} className="flex items-center gap-2 py-2 px-3 rounded-xl bg-white border border-stone-100">
                      <b.icon className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                      <span className="text-[11px] font-sans text-stone-600">{b.text}</span>
                    </div>
                  ))}
                </div>
                <Button data-testid="preview-create-btn" onClick={() => onCreateProduct(demo.genType, demo.genTopic, product.slug)} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-2xl py-6 font-sans font-bold text-base shadow-lg hover:shadow-xl transition-all mb-3">
                  Create this product <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <p className="text-[10px] font-sans text-stone-400 text-center">Free to create · No credit card required</p>
              </div>
            </div>
          </div>

          <div className="px-6 md:px-10 py-10 mt-6 border-t border-stone-100">
            <h3 className="text-xl font-serif font-bold text-stone-800 mb-6 text-center">{"What you'll get"}</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {demo.features.map(f => (
                <div key={f} className="flex items-center gap-2.5 p-3 rounded-xl bg-white border border-stone-100">
                  <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                  <span className="text-sm font-sans text-stone-700">{f}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Customization Value */}
          <div className="px-6 md:px-10 py-10 border-t border-stone-100 bg-[#F7F5F2]/50">
            <div className="text-center mb-6">
              <h3 className="text-xl font-serif font-bold text-stone-800 mb-2">Make it yours</h3>
              <p className="font-sans text-sm text-stone-500 max-w-md mx-auto">
                This is not a static file — personalize the design, content and details before you export.
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { icon: Palette, label: 'Your design', desc: 'Colors, fonts and style' },
                { label: 'Your content', desc: 'Text and personal details', icon: CheckCircle2 },
                { label: 'Your layout', desc: 'Sections and structure', icon: ChevronDown },
                { icon: Download, label: 'Your export', desc: 'Print-ready quality' },
              ].map((s, i) => (
                <div key={i} className="text-center py-3">
                  <s.icon className="w-5 h-5 text-[#B49A88] mx-auto mb-2" strokeWidth={1.5} />
                  <p className="font-serif text-xs font-bold text-stone-700 mb-0.5">{s.label}</p>
                  <p className="font-sans text-[10px] text-stone-400">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="px-6 md:px-10 py-10 border-t border-stone-100">
            <h3 className="text-xl font-serif font-bold text-stone-800 mb-6 text-center">Frequently asked questions</h3>
            <div className="space-y-2">
              {demo.faqs.map((faq, i) => (
                <div key={i} className="bg-white rounded-2xl border border-stone-100 overflow-hidden">
                  <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between p-4 text-left">
                    <span className="text-sm font-sans font-semibold text-stone-700 pr-4">{faq.q}</span>
                    <ChevronDown className={`w-4 h-4 text-stone-400 flex-shrink-0 transition-transform duration-200 ${openFaq === i ? 'rotate-180' : ''}`} />
                  </button>
                  {openFaq === i && <div className="px-4 pb-4"><p className="text-sm font-sans text-stone-500 leading-relaxed">{faq.a}</p></div>}
                </div>
              ))}
            </div>
          </div>

          <div className="px-6 md:px-10 py-10 border-t border-stone-100">
            <RecommendationSection
              placement="product_page"
              contextSlug={product.slug || product.title.toLowerCase().replace(/\s+/g, '-')}
              currency="usd"
              title="You might also like"
              limit={6}
            />
          </div>

          <div className="sticky bottom-0 left-0 right-0 md:hidden bg-[#B49A88]/95 backdrop-blur-md border-t border-stone-100 p-4">
            <Button data-testid="modal-sticky-cta" onClick={() => onCreateProduct(demo.genType, demo.genTopic, product.slug)} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-2xl py-5 font-sans font-bold shadow-lg">
              Create this product <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
